import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import Logo from "@/components/Logo";
import { Plus, Trash2, Save, Loader2, ArrowLeft, Boxes, MessageSquare, Inbox, Upload, ImageOff } from "lucide-react";
import { resolveImg } from "@/lib/img";

const EMPTY = {
  id: "",
  name: "",
  tag: "",
  description: "",
  image: "",
  price_from: 0,
  cta: "Explore",
  link: "",
  active: true,
  order: 99,
};

export default function AdminPage() {
  const { user, checking } = useAuth();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [demos, setDemos] = useState([]);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const loadAll = async () => {
    try {
      const [p, d, m] = await Promise.all([
        api.get("/products"),
        api.get("/admin/demo-requests").catch(() => ({ data: [] })),
        api.get("/admin/contact-messages").catch(() => ({ data: [] })),
      ]);
      setProducts(p.data || []);
      setDemos(d.data || []);
      setMessages(m.data || []);
    } catch (e) {
      setError(formatError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { if (user) loadAll(); }, [user]);

  if (checking) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center container-px py-24" data-testid="admin-denied">
        <div className="glass-card p-8 max-w-md text-center">
          <div className="eyebrow text-gold-300">Access denied</div>
          <div className="mt-2 font-serif text-2xl text-white">Admin only</div>
          <p className="mt-2 text-sm text-white/55">Your account ({user.email}) is not an administrator.</p>
          <Link to="/dashboard" className="mt-5 inline-flex pill pill-ghost text-sm">Back to dashboard</Link>
        </div>
      </div>
    );
  }

  const save = async () => {
    if (!draft.id.trim() || !draft.name.trim()) {
      setError("ID (slug) and Name are required.");
      return;
    }
    setSaving(true); setError("");
    try {
      await api.post("/products", { ...draft, price_from: Number(draft.price_from) || 0, order: Number(draft.order) || 99 });
      setDraft(EMPTY);
      setEditingId(null);
      await loadAll();
    } catch (e) {
      setError(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(`Delete product "${id}"?`)) return;
    try {
      await api.delete(`/products/${id}`);
      await loadAll();
    } catch (e) {
      setError(formatError(e.response?.data?.detail) || e.message);
    }
  };

  const edit = (p) => {
    setDraft({ ...p });
    setEditingId(p.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError("Image must be under 5MB."); return; }
    setUploading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDraft((d) => ({ ...d, image: data.url }));
    } catch (e2) {
      setError(formatError(e2.response?.data?.detail) || e2.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen container-px py-12" data-testid="admin-page">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-center justify-between mb-8">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <Logo />
        </div>

        <div className="eyebrow text-gold-300">AIBoxHub Admin</div>
        <h1 className="mt-2 font-serif text-4xl md:text-5xl text-white">Product & Inbox Management</h1>
        <p className="mt-2 text-sm text-white/55">
          Add new products, AI tools and SaaS modules without re-deploying. Updates appear instantly on the public site.
        </p>

        <div className="mt-8 flex flex-wrap gap-2">
          {[
            { id: "products", Icon: Boxes, label: `Products (${products.length})` },
            { id: "demos", Icon: Inbox, label: `Demo Requests (${demos.length})` },
            { id: "messages", Icon: MessageSquare, label: `Messages (${messages.length})` },
          ].map(({ id, Icon, label }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`admin-tab-${id}`}
              className={`pill text-sm ${tab === id ? "pill-gold" : "pill-ghost"}`}
            >
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>

        {tab === "products" && (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Editor */}
            <div className="lg:col-span-5">
              <div className="glass-card p-6">
                <div className="font-serif text-xl text-white">
                  {editingId ? "Edit product" : "Add new product"}
                </div>
                <div className="mt-1 text-xs text-white/45">
                  ID is a URL-safe slug (e.g. <code>ai-recruiter</code>). Existing IDs are updated.
                </div>
                <div className="mt-5 grid grid-cols-2 gap-3">
                  <F label="ID (slug)*" v={draft.id} on={(v) => setDraft({ ...draft, id: v })} testId="admin-prod-id" disabled={!!editingId} />
                  <F label="Order" type="number" v={draft.order} on={(v) => setDraft({ ...draft, order: v })} testId="admin-prod-order" />
                  <div className="col-span-2"><F label="Name*" v={draft.name} on={(v) => setDraft({ ...draft, name: v })} testId="admin-prod-name" /></div>
                  <F label="Tag" v={draft.tag} on={(v) => setDraft({ ...draft, tag: v })} testId="admin-prod-tag" />
                  <F label="Price from ($/mo)" type="number" v={draft.price_from} on={(v) => setDraft({ ...draft, price_from: v })} testId="admin-prod-price" />
                  <div className="col-span-2"><F label="Image URL (or upload below)" v={draft.image} on={(v) => setDraft({ ...draft, image: v })} testId="admin-prod-image" /></div>
                  <div className="col-span-2">
                    <div className="mb-1 text-[11px] font-mono uppercase tracking-[0.2em] text-white/50">Product image</div>
                    <div className="flex items-center gap-3">
                      <div className="h-16 w-16 rounded-lg overflow-hidden border border-white/10 bg-ink-300 shrink-0">
                        {draft.image
                          ? <img src={resolveImg(draft.image)} alt="" className="h-full w-full object-cover" />
                          : <div className="h-full w-full flex items-center justify-center text-white/30"><ImageOff className="h-4 w-4" /></div>}
                      </div>
                      <label className={`pill pill-ghost text-sm cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="admin-prod-upload-label">
                        {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                        {uploading ? "Uploading…" : "Upload image"}
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                          className="hidden"
                          onChange={onPickImage}
                          data-testid="admin-prod-upload-input"
                        />
                      </label>
                      {draft.image && (
                        <button
                          type="button"
                          onClick={() => setDraft({ ...draft, image: "" })}
                          className="pill text-xs !px-2.5 !py-1.5 border border-white/15 text-white/60 hover:text-white"
                          data-testid="admin-prod-image-clear"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <div className="mt-1.5 text-[11px] text-white/40">JPG/PNG/WebP/SVG. Max 5 MB.</div>
                  </div>
                  <div className="col-span-2"><F label="Description" v={draft.description} on={(v) => setDraft({ ...draft, description: v })} testId="admin-prod-desc" textarea /></div>
                  <F label="CTA label" v={draft.cta} on={(v) => setDraft({ ...draft, cta: v })} testId="admin-prod-cta" />
                  <F label="Link / URL" v={draft.link} on={(v) => setDraft({ ...draft, link: v })} testId="admin-prod-link" />
                  <label className="col-span-2 inline-flex items-center gap-2 text-sm text-white/70">
                    <input
                      data-testid="admin-prod-active"
                      type="checkbox"
                      checked={draft.active}
                      onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                      className="h-4 w-4 accent-gold-500"
                    />
                    Active (visible on site)
                  </label>
                </div>
                {error && <div className="mt-3 text-sm text-red-400" data-testid="admin-error">{error}</div>}
                <div className="mt-5 flex items-center gap-2">
                  <button onClick={save} disabled={saving} className="pill pill-gold text-sm disabled:opacity-50" data-testid="admin-prod-save">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingId ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />)}
                    {editingId ? "Update" : "Create"}
                  </button>
                  {editingId && (
                    <button onClick={() => { setDraft(EMPTY); setEditingId(null); }} className="pill pill-ghost text-sm" data-testid="admin-prod-cancel">
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* List */}
            <div className="lg:col-span-7">
              <div className="glass-card p-6">
                <div className="font-serif text-xl text-white">Current products ({products.length})</div>
                <div className="mt-1 text-xs text-white/45">
                  If empty, the site falls back to default seed products from the config.
                </div>
                <div className="mt-5 space-y-3">
                  {products.length === 0 && (
                    <div className="text-sm text-white/40">No products in DB yet — defaults are showing on the homepage.</div>
                  )}
                  {products.map((p) => (
                    <div key={p.id} className="rounded-xl border border-white/8 bg-ink-200/50 p-4 flex items-center gap-3" data-testid={`admin-prod-row-${p.id}`}>
                      <div className="h-12 w-12 rounded-lg overflow-hidden bg-ink-300 border border-white/10 shrink-0">
                        {p.image
                          ? <img src={resolveImg(p.image)} alt="" className="h-full w-full object-cover" loading="lazy" />
                          : <div className="h-full w-full bg-gold-500/10" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white font-medium truncate">{p.name}</div>
                        <div className="text-[11px] font-mono text-white/45">
                          {p.id} · {p.tag || "—"} · ${p.price_from || 0}/mo · order {p.order} {p.active ? "" : "· inactive"}
                        </div>
                      </div>
                      <button onClick={() => edit(p)} className="pill pill-ghost text-xs" data-testid={`admin-prod-edit-${p.id}`}>Edit</button>
                      <button onClick={() => remove(p.id)} className="pill !px-2.5 !py-1.5 text-xs border border-red-500/30 text-red-300 hover:bg-red-500/10" data-testid={`admin-prod-delete-${p.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "demos" && (
          <div className="mt-8 glass-card p-6" data-testid="admin-demos">
            <div className="font-serif text-xl text-white">Demo Requests</div>
            <div className="mt-4 space-y-3">
              {demos.length === 0 && <div className="text-sm text-white/40">No demo requests yet.</div>}
              {demos.map((d, i) => (
                <div key={i} className="rounded-xl border border-white/8 p-4">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <div className="text-white font-medium">{d.name} <span className="text-white/40">· {d.email}</span></div>
                    <div className="text-[11px] font-mono text-white/40">{new Date(d.created_at).toLocaleString()}</div>
                  </div>
                  <div className="text-xs text-white/60 mt-1">{d.product} · {d.company || "—"} · {d.phone || "—"}</div>
                  {d.message && <div className="mt-2 text-sm text-white/75">{d.message}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "messages" && (
          <div className="mt-8 glass-card p-6" data-testid="admin-messages">
            <div className="font-serif text-xl text-white">Contact Messages</div>
            <div className="mt-4 space-y-3">
              {messages.length === 0 && <div className="text-sm text-white/40">No messages yet.</div>}
              {messages.map((m, i) => (
                <div key={i} className="rounded-xl border border-white/8 p-4">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <div className="text-white font-medium">{m.name} <span className="text-white/40">· {m.email}</span></div>
                    <div className="text-[11px] font-mono text-white/40">{new Date(m.created_at).toLocaleString()}</div>
                  </div>
                  {m.subject && <div className="text-xs text-white/60 mt-1">Subject: {m.subject}</div>}
                  <div className="mt-2 text-sm text-white/75">{m.message}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function F({ label, v, on, type = "text", testId, textarea, disabled }) {
  return (
    <div>
      <div className="mb-1 text-[11px] font-mono uppercase tracking-[0.2em] text-white/50">{label}</div>
      {textarea ? (
        <textarea
          data-testid={testId}
          rows={2}
          value={v ?? ""}
          onChange={(e) => on(e.target.value)}
          className="w-full bg-ink-200 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-gold-500/50"
        />
      ) : (
        <input
          data-testid={testId}
          type={type}
          disabled={disabled}
          value={v ?? ""}
          onChange={(e) => on(type === "number" ? e.target.value : e.target.value)}
          className="w-full bg-ink-200 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-gold-500/50 disabled:opacity-50"
        />
      )}
    </div>
  );
}
